import Link from 'next/link'
import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { Button } from '@components/UI/Button'
import { ArrowRightIcon, ClockIcon } from '@heroicons/react/solid'
import { HandshakeIcon } from '@components/UI/Icons/handshake'
import { BullseyeIcon } from '@components/UI/Icons/bullseye'
import { ChartLineIcon } from '@components/UI/Icons/chartLine'

export default function PropertyInvestors() {
  return (
    <>
      <NextSeo
        title="Next Day Home Repair Pricing for Property Investors "
        description="Save time after your next home inspection by getting zip code specific repair pricing estimates. Negotiate using accurate data. Get started."
        canonical="https://majordomo.com/repair-pricing/-property-investors"
        openGraph={{
          url: 'https://majordomo.com/repair-pricing/property-investors',
          title:
            'Next Day Home Repair Pricing for Property Investors | Majordomo',
          description:
            'Save time after your next home inspection by getting zip code specific repair pricing estimates. Negotiate using accurate data. Get started.',
          images: [
            {
              url:
                'https://majordomo.com/images/product-pages/investors-hero.png',
              width: 700,
              height: 681,
              alt: 'A row of five red Monopoly hotel game pieces'
            },
            {
              url: 'https://majordomo.com/images/product-pages/domoreport.png',
              width: 1816,
              height: 1992,
              alt: 'Screen shot of Majordomo’s Domoreport'
            }
          ]
        }}
      />
      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <div className="flex md:flex-row flex-col justify-between items-center lg:pb-36 pb-12">
          <div className="flex flex-col md:items-start items-center justify-center w-full 2xl:p-36 xl:p-20 md:p-8 md:order-first order-last">
            <div className="flex items-center border-b border-gray-200 mb-4 pb-4 md:w-full">
              <ChartLineIcon className="pr-2 mr-2 border-r border-gray-200 w-8 h-8 fill-current text-brand" />
              <h1 className="text-2xl font-black tracking-wide text-brand uppercase">
                Next Day Home Repair Pricing for Property Investors
              </h1>
            </div>

            <p className="mb-8 xl:text-3xl lg:text-2xl text-xl font-light">
              <span className="font-black">Faster</span> deals
              <br />& <span className="font-black">Higher</span> cash flow
            </p>

            <p className="md:w-full md:p-0 px-8">
              Integrate Majordomo into your property acquisition system so you
              can <span className="font-black">let Majordomo work for you</span>
              . You're then free to focus on more high-touch and profitable
              activities that offer better use of your time, energy, skills and
              expertise – all while you{' '}
              <span className="font-black">generate higher cash flow</span>.
            </p>
          </div>

          <img
            src="/images/product-pages/investors-hero.png"
            className="md:order-last order-first md:mb-0 mb-8"
            width="50%"
          />
        </div>

        <div className="mb-0 lg:mb-36 sm:mb-12">
          <h4 className="max-w-md px-8 pb-8 mx-auto text-lg font-black tracking-tight text-center md:text-2xl md:px-0 lg:pb-0">
            Transform your acquisition strategy after the inspection
          </h4>

          <div className="flex flex-col px-4 lg:flex-row justify-evenly lg:px-16">
            <div className="flex flex-col items-center order-last w-full p-4 space-y-12 md:p-16 lg:w-1/2 lg:order-first">
              <div className="relative flex">
                <ClockIcon
                  width={56}
                  className="absolute top-0 w-12 h-12 p-2 text-white md:-left-24 md:w-16 md:h-16 bg-gold-400"
                />

                <div>
                  <h4 className="max-w-lg mt-16 mb-2 mr-auto text-lg font-black tracking-tight md:text-2xl md:mt-0">
                    Save time after the inspection
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Our guaranteed 24-hour turnaround allows you to focus sooner
                    on the cash-flow analysis and start negotiations sooner.{' '}
                    <span className="font-black">
                      No more running around or waiting for contractors to
                      return calls
                    </span>
                    . Also available is our simple CSV download so you can
                    intregate Majordomo directly into your existing systems.{' '}
                    <a
                      href="https://intercom.help/majordomo/en/articles/4220719-how-do-i-download-the-domoreport-s-data"
                      className="text-accentBlue hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </div>

              <div className="relative flex">
                <BullseyeIcon className="absolute top-0 w-12 h-12 p-2 text-white fill-current md:-left-24 md:w-16 md:h-16 bg-gold-400" />

                <div>
                  <h4 className="max-w-lg mt-16 mb-2 mr-auto text-lg font-black tracking-tight md:text-2xl md:mt-0">
                    Precise local estimates, reviewed by humans
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Majordomo's repair-estimate data comes from analysts who
                    study contractor and insurance invoices for 20,000 projects
                    from 35,000 providers for material, equipment, labor and
                    installation costs across the US. Every Domoreport is
                    reviewed by an industry expert and will give you a head
                    start on estimating your underwriting repair costs.{' '}
                    <a
                      href="/blog/majordomos-repair-cost-estimates-are-local-and-accurate/"
                      className="text-accentBlue hover:underline"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </div>

              <div className="relative flex">
                <HandshakeIcon className="absolute top-0 w-12 h-12 p-2 text-white fill-current md:-left-24 md:w-16 md:h-16 bg-gold-400" />

                <div>
                  <h4 className="max-w-lg mt-16 mb-2 mr-auto text-lg font-black tracking-tight md:text-2xl md:mt-0">
                    Negotiate smarter with sellers
                  </h4>
                  <p className="max-w-lg mr-auto">
                    The Domoreport's ZIP Code specific repair estimates are
                    organized into a simple and clear report that outlines
                    project details and cost estimates, so negotiations can be
                    based on facts, not guesses. Use our "Request List" tool to
                    build a customized request list to send to the sellers;{' '}
                    <span className="font-black">
                      no more cut-and-paste addendum projects
                    </span>
                    . Save your custom request list addendum PDF for a
                    professional edge when negotiating for seller concessions.{' '}
                    <a
                      href="https://intercom.help/majordomo/en/articles/4641002-how-do-i-create-and-use-the-request-list"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accentBlue hover:underline"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </div>

              <div className="relative flex self-start ml-0 2xl:ml-8 lg:ml-0 md:ml-12 sm:ml-8">
                <h4 className="max-w-lg mb-2 text-lg font-black tracking-tight md:text-2xl">
                  All for just $99
                  <span className="text-base font-light"> / per report</span>
                </h4>
              </div>
            </div>

            <div className="flex items-center justify-center order-first w-full lg:w-1/2 lg:p-16 lg:order-last">
              <img
                src="/images/product-pages/domoreport.png"
                alt="Majordomo product samples"
                className="max-w-xs xl:max-w-xl lg:max-w-md"
              />
            </div>
          </div>

          <div className="flex flex-col items-center w-full pt-8 pb-0 md:flex-row justify-evenly md:space-y-0 space-y-4 md:pb-12 md:pt-0">
            <Link href="/checkout">
              <Button variant="primary" clipSide="right">
                Get repair estimates{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </Button>
            </Link>

            <Link href="/features/domoreport-repair-estimates">
              <a className="flex items-center justify-center font-bold text-accentBlue hover:underline">
                Learn More{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </a>
            </Link>
          </div>

          <div className="flex flex-col items-center w-full pt-8 pb-0 md:flex-row justify-evenly md:space-y-0 space-y-4 md:pb-12 md:pt-0">
            <p>
              Interested in bulk purchases?{' '}
              <Link href="/contact-us">
                <a className="text-accentBlue hover:underline">Contact us</a>
              </Link>
            </p>
          </div>
        </div>

        <div className="flex flex-col mb-12 lg:flex-row justify-evenly lg:mb-36">
          <div className="p-8 mt-0 text-gray-300 bg-black lg:mt-32 lg:w-1/2 md:p-16 space-y-8">
            <h4 className="text-xl font-black tracking-tight text-white md:text-3xl">
              Majordomo Is On Your Team
            </h4>

            <div>
              <h4 className="mb-2 text-lg font-black tracking-tight text-white md:text-2xl">
                Additional resources
              </h4>

              <p>
                <b>Explore:</b> We offer a full page of online resources,
                specific to your local area, to help buyers. The information
                includes homeowner rebates, permits, maps, utilities and much
                more.{' '}
                <Link href="/features/explore-homeowner-resources">
                  <a className="text-accentBlue hover:underline">Learn more</a>
                </Link>
              </p>
            </div>

            <p>
              <b>Documents:</b> Save your home inspection report, photos and
              other important documents to Majordomo Documents and access them
              from any web browser; this service is free forever with every
              Domoreport.{' '}
              <Link href="/features/upload-property-documents">
                <a className="text-accentBlue hover:underline">Learn more</a>
              </Link>
            </p>

            <p>
              <b>Integrated Request List:</b> Build a custom request list of
              repair requests, credit amounts, and items accepted as-is. Export
              a custom addendum.{' '}
              <a
                href="https://intercom.help/majordomo/en/articles/4641002-how-do-i-create-and-use-the-request-list"
                className="text-accentBlue hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </p>

            <div>
              <h4 className="mb-2 text-lg font-black tracking-tight text-white md:text-2xl">
                Easy to share
              </h4>

              <p>
                Built-in tools easily allow you to share the Domoreport and
                collaborate during the inspection review period.{' '}
                <a
                  href="https://intercom.help/majordomo/en/articles/3216497-how-do-i-share-my-domoreport"
                  className="text-accentBlue hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </a>
              </p>
            </div>

            <div>
              <h4 className="mb-2 text-lg font-black tracking-tight text-white md:text-2xl">
                Always available
              </h4>

              <p>
                Access Majordomo on your computer, smartphone or tablet anytime.
                Have a question? Majordomo is ready to help with 24-hour chat
                and customer support.
              </p>
            </div>
          </div>

          <div className="items-center justify-center hidden w-full p-4 lg:flex folded-l-max lg:w-1/2 bg-gray-50">
            <img
              src="/images/product-pages/on-your-team.png"
              alt="A Majordomo"
              className="object-contain max-w-md"
            />
          </div>
        </div>
      </main>

      <Footer />
    </>
  )
}
